export const anatomyModule = {
  id: "anatomy",
  title: "Kidney Anatomy: Part 1",
  sections: [
    {
      type: "text",
      content: `<h3>Kidney Size & Shape</h3>
      <p>The kidneys are located <span class="glossary-term">retroperitoneally<span class="tooltip">anatomical space that is located behind the peritoneum, which is a membrane that lines the abdominal cavity and covers most of the abdominal organs</span></span> in the abdomen on 
      the posterior abdominal wall on either side of the vertebral column.</p>
      <p>The kidneys are located  at the level of the <b>T12 and L3 vertebrae</b> and covered partially by <b>ribs 11 and 12</b>. 
      </p>
      <p>
      It is about <b>10 x 5 x 2-3 cm (roughly a fist)</b> and weighs each about <b>160 gr in men and 135 gr in women.</b></p><p>
      The left kidney is usually slightly larger and higher placed than the right kidney, because of the liver that 
      occupies space on the right side. Some people have an extra kidney, but this is extremely rare. </p>
      <p>
      The kidneys are <b>redbrown</b> in color and most often have a <b>smooth surface</b>. However, there are some 
      individuals that have kidneys with a lobulated surface.</p>
<p>This is because the kidneys are formed from multiple lobes during fetal development, and most often by years 3 to 5 of age these
lobes fuse together. However, if they do not fully fuse you end up with kidneys that have a lobulated surface. This is called
       <b class="purple-term">persistent fetal lobulation</b>. 
       </p>
       <p>Persistent fetal lobulation can sometimes be misinterpreted as kidney scarring on imaging, especially
       if the indentations are deep and irregular. However, persistent fetal lobulation typically has a smooth and regular appearance, 
       while scarring often appears as irregular and asymmetric indentations.
     </p>
        <p>
       Indentation that are not due to persistent fetal lobulation are a sign of <b>scarring</b>, which can occur due to previous infections, injury or ischemia.
     </p>
       <div class="highlight-box">
          <strong>Quick Facts:</strong>
          <ul>
            <li>📏 Size: About <b>10 cm</b> long, <b>5 cm</b> wide, <b>3 cm</b> thick</li>
            <li>⚖️ Weight: <b>160 grams</b> in men and <b>135 grams</b> in women</li>
            <li>📍 Location: <b>Retroperitoneal</b> at the level of <b>T12 to L3 vertebrae</b> and partially covered by <b>ribs 11 and 12</b>.</li>
            <li>🎨 Color: Redbrown in appearance</li>
          </ul>
        </div>
        `,

      image: "/images/kidney/adrenalglandveins.png",
    },
    {
      type: "question",
      text: "what is the size of the kidney?",
      options: [
        "12 x 4 x 3 cm",
        "12 x 5 x 3 cm",
        "10 x 5 x 3 cm",
        "10 x 4 x 3 cm",
      ],
      correct: 2,
      explanation:
        "The correct answer is 10 x 5 x 3 cm. The kidneys are about 10 cm long, 5 cm wide, and 3 cm thick.",
    },
    {
      type: "question",
      text: "which kidney is usually larger and higher placed?",
      options: ["Left", "Right"],
      correct: 0,
      explanation:
        "The left kidney is usually larger and positioned higher than the right kidney due to the presence of the liver on the right side.",
    },
    {
      type: "question",
      text: "Are the kidneys placed retroperitoneally?",
      options: ["Yes", "No"],
      correct: 0,
      explanation:
        "The kidneys are located in the retroperitoneal space, meaning they lie behind the peritoneum.",
    },
    {
      type: "flashcard",
      front: "What does retroperitoneal mean?",
      back: "Located behind the peritoneum - the membrane that lines the abdominal cavity",
      hint: "Think about the prefix 'retro' meaning behind",
    },
    {
      type: "text",
      content: `<h3>Protective Layers</h3>
      <p><b class="purple-term">The renal fascia</b> is a thin and see-through layer of connective tissue (collagen) that surrounds the kidneys and adrenal glands.
      The fascia helps to anchor the kidneys to surrounding structures and provides a protective barrier.</p>
      <p>Next below the renal fascia is <b class="purple-term">the perirenal fat</b>, which is a layer of adipose tissue 
      that surrounds the kidneys and helps to cushion and protect them from injury. The perirenal fat also helps to insulate the kidneys 
      and maintain their temperature.</p>
       <p>
       In patients with severe weight loss, <b>the perirenal fat</b> can be depleted, which can lead to increased risk of kidney injury, for example if the person falls, as there is no longer a cushion around the kidney to lessen the fall. 
       This can be seen in patients with severe malnutrition, such as those with anorexia nervosa or advanced cancer (patients with cachexia).
       </p>
 <p>Under the perirenal fat is the <b class="purple-term">fibrous capsule</b> that surrounds the kidney and provides a protective barrier. During
 dissection, this capsule can be easily peeled off.</p>
        <p>Inside the kidney you find: the <b class="purple-term">cortex</b>, the <b class="purple-term">medulla</b> and the <b class="purple-term">pelvis</b>. 
       <div class="highlight-box">
          <strong>The layers</strong>
          <ul>
            <li>Renal fascia</li>
            <li>Perirenal fat</li>
            <li>Renal capsule</li>
                <li>Kidney parenchyma:<ul>
                <li>Cortex</li>
                <li>Medulla</li>
                <li>Pelvis</li></ul>
                </li>
          </ul>
        </div>
        `,
      image: "/images/kidney/kidneylayers.png",
    },
    {
      type: "ordering",
      question: "Arrange the kidney's protective layers from outermost to innermost:",
      items: ["Renal fascia", "Perirenal fat", "Fibrous capsule"],
      explanation: "From outermost to innermost: Renal fascia (anchors the kidney) → Perirenal fat (cushions the kidney) → Fibrous capsule (directly surrounds the kidney parenchyma).",
    },
    {
      type: "question",
      text: "The perirenal fat is important as",
      options: [
        "it provides nutrients",
        "protects the renal arteries",
        "has no function",
        "insulates the kidneys",
      ],
      correct: 3,
      explanation:
        "The perirenal fat is adepose tissue that protect the kidney from injury and also helps to insulate the kidneys and maintain their temperature",
    },
    {
      type: "question",
      text: "Why are patients with severe weight loss at increased risk of kidney injury?",
      options: [
        "The renal fascia becomes thickened and restricts the kidney movement",
        "The fibrous capsule becomes more fragile",
        "The cortex and medulla lose their structural integrity",
        "The perirenal fat is depleted, reducing cushioning around the kidney",
      ],
      correct: 3,
      explanation:
        "In patients with severe weight loss, the perirenal fat can be depleted, which reduces the cushioning around the kidney and increases the risk of injury.",
    },
    {
      type: "question",
      text: "What are the three protective layers surrounding the kidney from outermost to innermost?",
      options: [
        "Fibrous capsule => Perirenal fat => Renal fascia",
        "Renal fascia => Perirenal fat => Fibrous capsule",
        "Perirenal fat => Renal fascia => Fibrous capsule",
        "Renal fascia => Fibrous capsule => Perirenal fat",
      ],
      correct: 1,
      explanation:
        "The three protective layers surrounding the kidney from outermost to innermost are: Renal fascia, Perirenal fat, and Fibrous capsule.",
    },
    {
      type: "text",
      content: `<h3>The kidneys</h3>
      <p>Under the renal capsule, you will find the kidney itself, first the <b class="purple-term">
      cortex</b> and then the <b class="purple-term">medulla</b> and at last the <b class="purple-term">pelvis</b>.</p>
      <p>Macroscopically the cortex is darkbrown, while the medulla has a more pale color.</p>
      <div class="highlight-box"><ul>
      <li>Cortex</li>
      <li>Medulla
      <ul>
      <li>Renal pyramids</li>
      <li>Renal papillae</li>
      </ul></li>
      <li>Pelvis
      <ul>
       <li>Minor calyces</li>
      <li>Major calyces</li>
      <li>Pelvis</li>
      </ul></li>
      <li>Ureter</li>
      </ul></div>
       <p>The smallest functional unit of the kidney is the <b class="purple-term">nephron</b>, and each kidney has about <b>one million of them</b>.</p> 
        `,
      image: "/images/kidney/kidneystructure.png",
    },
    {
      type: "question",
      text: "How many nephrons are there approximately in each kidney?",
      options: ["100.000", "500.000", "1.000.000", "2.000.000"],
      correct: 2,
      explanation:
        "The nephron is the smallest functional unit of the kidney, each kidney has about 1 million af them!",
    },
  ],
};
